import { LitElement, html, css, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api, HttpError } from '../api/client';
import type { SharedDeck } from '../api/types';
import {
  getOrCreateViewerId,
  getSavedViewerName,
  saveViewerName,
  markSlideSeen,
} from '../lib/share-viewer';
import { setDeckTitle } from '../lib/page-title';
import { collectImageUrls, warmImages } from '../lib/preload-images';

/**
 * Anonymous share view (sharing v2). Resolves the opaque share token to a deck
 * payload (HTML + lightweight metadata) and renders it in an iframe. Deliberately
 * omits the back-to-list and share buttons — anonymous viewers shouldn't see the
 * rest of the library or fan out new shares. Replaces the persistent nav-bar by
 * covering it; users still get the deck's own theme toggle inside the iframe.
 *
 * Sharing v2 adds, in order:
 *   1. PASSCODE GATE — when getShare reports the share is protected (or the API
 *      401s), a passcode card is shown; on verify the deck loads.
 *   2. LANDING + OPTIONAL NAME PROMPT — before the deck shows, a small centred
 *      card offers an optional name and a one-line how-to-navigate hint. The
 *      name persists; Skip proceeds anonymously.
 *   3. PRESENT BY DEFAULT — once the deck iframe loads, the page posts
 *      `{ __omelette_presenting: true }` so the deck opens in presentation mode.
 *   4. ANALYTICS — an `open` event is recorded once the deck shows, and a unique
 *      per-session `slide` event is recorded the first time each slide is seen
 *      (relayed up from the in-iframe deck-bridge.js as `deck:slidechange`). All
 *      reporting is fire-and-forget.
 */

type Phase =
  | 'loading'
  | 'gate'
  | 'landing'
  | 'preloading'
  | 'deck'
  | 'not-found'
  | 'expired'
  | 'error';

@customElement('shared-deck-page')
export class SharedDeckPage extends LitElement {
  @property({ attribute: 'token' }) token = '';

  /** The resolved deck payload, once available (post-verify for protected shares). */
  @state() private deck: SharedDeck | null = null;
  /** Drives which card / surface renders. */
  @state() private phase: Phase = 'loading';
  /** Title surfaced on the gate card (from the server's gate response). */
  @state() private gateTitle = '';
  /** Bound to the passcode input on the gate. */
  @state() private passcodeInput = '';
  /** Inline error under the passcode input (e.g. "Incorrect passcode"). */
  @state() private gateError: string | null = null;
  /** True while a verify request is in flight. */
  @state() private verifying = false;
  /** Bound to the optional name input on the landing card. */
  @state() private nameInput = '';
  /** Generic error message for the error phase. */
  @state() private errorMsg: string | null = null;
  /** Image-preload progress (preloading phase). */
  @state() private preloadDone = 0;
  @state() private preloadTotal = 0;

  /** Stable per-browser viewer id (analytics grouping). */
  private viewerId = '';
  /** The viewer's name, or null when anonymous. */
  private viewerName: string | null = null;
  /** Slide indices already reported this session (unique-per-session dedupe). */
  private seenSlides = new Set<number>();
  /** Guards against double-recording the `open` event. */
  private openRecorded = false;

  static styles = css`
    :host {
      display: block;
      position: fixed;
      inset: 0;
      background: var(--canvas);
      z-index: 20;
    }
    iframe {
      width: 100%;
      height: calc(100% - 32px);
      border: 0;
      display: block;
    }
    .footer {
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.75rem;
      color: var(--text-secondary);
      background: var(--surface);
      border-top: 1px solid var(--border-subtle);
    }
    .state {
      padding: 64px 32px;
      text-align: center;
      color: var(--text-secondary);
    }
    .state.error { color: var(--danger); }

    /* Centred card surface for the gate + landing steps. */
    .centre {
      position: absolute;
      inset: 0 0 32px 0; /* leave the footer visible */
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
    }
    .card {
      width: 100%;
      max-width: 420px;
      background: var(--surface);
      border: 1px solid var(--border-subtle);
      border-radius: 12px;
      padding: 28px;
      box-shadow: var(--shadow-hover, none);
      text-align: left;
    }
    .card h1 {
      font-family: var(--font-editorial, Georgia, serif);
      font-weight: 400;
      font-size: 1.5rem;
      letter-spacing: -0.01em;
      margin: 0 0 8px;
      color: var(--text-primary);
      line-height: 1.25;
    }
    .card .hint {
      color: var(--text-secondary);
      font-size: 0.9rem;
      line-height: 1.45;
      margin: 0 0 18px;
    }
    .card label {
      display: block;
      font-size: 0.78rem;
      font-weight: 600;
      letter-spacing: 0.01em;
      text-transform: uppercase;
      color: var(--text-secondary);
      margin-bottom: 6px;
    }
    .card input {
      width: 100%;
      font: inherit;
      font-size: 0.95rem;
      color: var(--text-primary);
      background: var(--canvas);
      border: 1px solid var(--border-subtle);
      border-radius: 6px;
      padding: 10px 12px;
      margin-bottom: 16px;
    }
    .card input:focus {
      outline: none;
      border-color: var(--text-secondary);
    }
    .actions {
      display: flex;
      gap: 10px;
      align-items: center;
    }
    .btn-primary {
      display: inline-flex;
      align-items: center;
      height: 38px;
      padding: 0 18px;
      background: var(--ink);
      color: var(--on-ink);
      border: 1px solid var(--ink);
      border-radius: 6px;
      font-size: 0.9rem;
      font-weight: 500;
      cursor: pointer;
    }
    .btn-primary[disabled] { opacity: 0.55; cursor: not-allowed; }
    .btn-ghost {
      display: inline-flex;
      align-items: center;
      height: 38px;
      padding: 0 16px;
      background: transparent;
      color: var(--text-secondary);
      border: 1px solid var(--border-subtle);
      border-radius: 6px;
      font-size: 0.9rem;
      cursor: pointer;
    }
    .btn-ghost:hover { color: var(--text-primary); border-color: var(--border-default); }
    .field-error {
      color: var(--danger);
      font-size: 0.85rem;
      margin: -8px 0 14px;
    }
    .lock-line {
      display: flex;
      align-items: center;
      gap: 8px;
      color: var(--text-secondary);
      font-size: 0.9rem;
      line-height: 1.45;
      margin: 0 0 18px;
    }
    /* Branded loader / preload / state surfaces. */
    .brandload {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      text-align: center;
      padding: 24px;
    }
    .brandload .mark {
      color: var(--text-primary);
      animation: brandload-pulse 2.4s ease-in-out infinite;
    }
    .brandload-bar {
      position: relative;
      width: 184px;
      height: 4px;
      border-radius: 999px;
      background: var(--surface-hover, var(--border-subtle));
      overflow: hidden;
    }
    .brandload-bar span {
      display: block;
      height: 100%;
      width: 0;
      border-radius: inherit;
      background: var(--brand-primary, var(--ink));
      transition: width 200ms ease;
    }
    .brandload-bar.indet span {
      width: 42%;
      animation: brandload-slide 1.15s ease-in-out infinite;
    }
    .brandload-text {
      color: var(--text-secondary);
      font-size: 0.85rem;
      margin: 0;
    }
    .state-title {
      font-family: var(--font-editorial, Georgia, serif);
      font-weight: 400;
      font-size: 1.4rem;
      color: var(--text-primary);
      margin: 0;
    }
    @keyframes brandload-slide {
      0% { transform: translateX(-120%); }
      100% { transform: translateX(320%); }
    }
    @keyframes brandload-pulse {
      0%, 100% { opacity: 0.82; }
      50% { opacity: 1; }
    }
    /* Portrait phones can't reflow the fixed 1920x1080 deck — nudge to rotate.
       The pill auto-fades after a few seconds and is portrait-only. */
    .rotate-hint { display: none; }
    @media (orientation: portrait) and (pointer: coarse) {
      .rotate-hint {
        display: block;
        position: fixed;
        top: calc(10px + env(safe-area-inset-top, 0px));
        left: 50%;
        transform: translateX(-50%);
        z-index: 30;
        background: var(--surface);
        color: var(--text-secondary);
        border: 1px solid var(--border-subtle);
        border-radius: 999px;
        padding: 7px 16px;
        font-size: 0.82rem;
        white-space: nowrap;
        box-shadow: var(--shadow-overlay, 0 2px 10px rgba(0, 0, 0, 0.3));
        animation: rotate-hint-in 0.3s ease both, rotate-hint-out 0.6s ease 5s forwards;
      }
    }
    @keyframes rotate-hint-in { from { opacity: 0; } to { opacity: 1; } }
    @keyframes rotate-hint-out { to { opacity: 0; visibility: hidden; } }
  `;

  connectedCallback() {
    super.connectedCallback();
    // Stable identity + any previously saved name, established before load so an
    // immediately-shown deck (e.g. returning viewer with a saved name) records
    // analytics with the right viewer.
    try {
      this.viewerId = getOrCreateViewerId(window.localStorage);
      this.viewerName = getSavedViewerName(window.localStorage);
    } catch {
      this.viewerId = '';
      this.viewerName = null;
    }
    window.addEventListener('message', this.onWindowMessage);
    void this.load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('message', this.onWindowMessage);
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('token')) {
      // Reset per-token session state so navigating between shares is clean.
      this.seenSlides = new Set<number>();
      this.openRecorded = false;
      this.deck = null;
      void this.load();
    }
  }

  private async load() {
    if (!this.token) {
      this.phase = 'error';
      this.errorMsg = 'Missing share token';
      return;
    }
    this.phase = 'loading';
    this.gateError = null;
    this.errorMsg = null;
    setDeckTitle(undefined, false); // plain "Folio" until the deck resolves
    try {
      const res = await api.getShare(this.token);
      if ('requiresPasscode' in res && res.requiresPasscode === true) {
        this.gateTitle = res.title ?? '';
        if (this.gateTitle) setDeckTitle(this.gateTitle);
        this.phase = 'gate';
        return;
      }
      // Unprotected (or already-verified) share — we have the deck. The gate
      // shape is excluded above; narrow to SharedDeck for the assignment.
      this.deck = res as SharedDeck;
      this.proceedAfterDeckResolved();
    } catch (err) {
      this.handleLoadError(err);
    }
  }

  /** Map a load/verify error to the right phase. 401 -> passcode gate; 404 ->
   *  not-found; 410 -> expired; anything else -> generic error. */
  private handleLoadError(err: unknown) {
    if (err instanceof HttpError) {
      if (err.status === 401) {
        // The API insists on a passcode but didn't pre-announce a gate — show it.
        this.phase = 'gate';
        return;
      }
      if (err.status === 404) {
        this.phase = 'not-found';
        return;
      }
      if (err.status === 410) {
        this.phase = 'expired';
        return;
      }
    }
    this.phase = 'error';
    this.errorMsg = err instanceof Error ? err.message : 'Failed to load shared deck';
  }

  /** Once we hold a deck payload, decide whether to show the landing/name step
   *  first or jump straight to the deck. */
  private proceedAfterDeckResolved() {
    if (this.deck) setDeckTitle(this.deck.title);
    if (this.viewerName) {
      // Returning / already-named viewer — warm images, then show the deck.
      void this.startPreload();
    } else {
      this.phase = 'landing';
    }
  }

  /** Warm every deck image before revealing the iframe so presenting is smooth
   *  — no first-time image fetch mid-slide. Falls through immediately when the
   *  deck has no images; a 6s cap guarantees we never strand the viewer. */
  private async startPreload() {
    const urls = collectImageUrls(this.deck?.html ?? '');
    if (urls.length === 0) {
      this.phase = 'deck';
      return;
    }
    this.preloadDone = 0;
    this.preloadTotal = urls.length;
    this.phase = 'preloading';
    await warmImages(urls, {
      timeoutMs: 6000,
      onProgress: (done, total) => {
        this.preloadDone = done;
        this.preloadTotal = total;
      },
    });
    this.phase = 'deck';
  }

  // ─── Passcode gate ──────────────────────────────────────────────────────
  private async verifyPasscode(e?: Event) {
    if (e) e.preventDefault();
    if (this.verifying) return;
    const code = this.passcodeInput.trim();
    if (!code) {
      this.gateError = 'Enter the passcode you were given.';
      return;
    }
    this.verifying = true;
    this.gateError = null;
    try {
      const deck = await api.verifyShare(this.token, code);
      this.deck = deck;
      this.passcodeInput = '';
      this.proceedAfterDeckResolved();
    } catch (err) {
      if (err instanceof HttpError && err.status === 401) {
        this.gateError = 'Incorrect passcode';
      } else if (err instanceof HttpError && err.status === 404) {
        this.phase = 'not-found';
      } else if (err instanceof HttpError && err.status === 410) {
        this.phase = 'expired';
      } else {
        this.gateError = err instanceof Error ? err.message : 'Verification failed';
      }
    } finally {
      this.verifying = false;
    }
  }

  // ─── Landing + name prompt ──────────────────────────────────────────────
  private viewWithName(e?: Event) {
    if (e) e.preventDefault();
    const name = this.nameInput.trim();
    if (name) {
      this.viewerName = name;
      try {
        saveViewerName(window.localStorage, name);
      } catch {
        /* private mode — proceed without persistence */
      }
    }
    void this.startPreload();
  }

  private skipLanding(e?: Event) {
    if (e) e.preventDefault();
    // Proceed anonymously — do NOT persist a name.
    this.viewerName = null;
    void this.startPreload();
  }

  // ─── Deck iframe + presentation + analytics ─────────────────────────────
  private onIframeLoad = (e: Event) => {
    const iframe = e.target as HTMLIFrameElement;
    const win = iframe.contentWindow;
    if (!win) return;
    try {
      // Match the existing theme handshake, then present-by-default.
      const theme = this.currentTheme();
      win.postMessage({ type: 'deck:theme', theme }, '*');
      // The deck-stage enters presentation mode (rail hidden, slide fills the
      // frame) on receiving this exact message.
      win.postMessage({ __omelette_presenting: true }, '*');
    } catch {
      /* cross-origin — best effort */
    }
    // Record the open exactly once.
    this.recordOpen();
  };

  private currentTheme(): 'light' | 'dark' {
    const t = document.documentElement.getAttribute('data-theme');
    return t === 'dark' ? 'dark' : 'light';
  }

  private recordOpen() {
    if (this.openRecorded) return;
    this.openRecorded = true;
    // Fire-and-forget — the client swallows errors.
    void api.recordShareEvent(this.token, {
      type: 'open',
      viewerId: this.viewerId,
      ...(this.viewerName ? { name: this.viewerName } : {}),
    });
  }

  private onWindowMessage = (e: MessageEvent) => {
    const data = e.data;
    if (!data || typeof data !== 'object') return;
    if (data.type !== 'deck:slidechange') return;
    const index = data.index;
    // Only record the FIRST time each slide index is seen this session.
    if (!markSlideSeen(this.seenSlides, index)) return;
    void api.recordShareEvent(this.token, {
      type: 'slide',
      slideIndex: Math.trunc(index),
      viewerId: this.viewerId,
      ...(this.viewerName ? { name: this.viewerName } : {}),
    });
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  private footer(): TemplateResult {
    return html`<div class="footer">Shared via @rdx/deck-system</div>`;
  }

  private renderGate(): TemplateResult {
    return html`
      <div class="centre">
        <form class="card" @submit=${this.verifyPasscode}>
          <h1>${this.gateTitle || 'Passcode required'}</h1>
          <p class="lock-line">
            This deck is passcode-protected — enter the passcode you were given.
          </p>
          <label for="passcode">Passcode</label>
          <input
            id="passcode"
            type="password"
            autocomplete="off"
            .value=${this.passcodeInput}
            @input=${(ev: Event) => (this.passcodeInput = (ev.target as HTMLInputElement).value)}
            placeholder="Enter passcode"
          />
          ${this.gateError ? html`<div class="field-error">${this.gateError}</div>` : nothing}
          <div class="actions">
            <button type="submit" class="btn-primary" ?disabled=${this.verifying}>
              ${this.verifying ? 'Verifying…' : 'Verify'}
            </button>
          </div>
        </form>
      </div>
      ${this.footer()}
    `;
  }

  private renderLanding(): TemplateResult {
    return html`
      <div class="centre">
        <form class="card" @submit=${this.viewWithName}>
          <h1>You've been shared a deck</h1>
          <p class="hint">
            Use arrow keys, click, or Space to move through the slides. Press F for fullscreen.
          </p>
          <label for="viewer-name">Your name (optional)</label>
          <input
            id="viewer-name"
            type="text"
            autocomplete="name"
            .value=${this.nameInput}
            @input=${(ev: Event) => (this.nameInput = (ev.target as HTMLInputElement).value)}
            placeholder="Your name (optional)"
          />
          <div class="actions">
            <button type="submit" class="btn-primary">View deck</button>
            <button type="button" class="btn-ghost" @click=${this.skipLanding}>Skip</button>
          </div>
        </form>
      </div>
      ${this.footer()}
    `;
  }

  private renderDeck(): TemplateResult {
    const deck = this.deck as SharedDeck;
    return html`
      <iframe
        title=${deck.title}
        srcdoc=${deck.html}
        sandbox="allow-scripts allow-popups allow-forms allow-modals"
        allow="fullscreen"
        @load=${this.onIframeLoad}
      ></iframe>
      <div class="rotate-hint">↻ Rotate for the best view</div>
      ${this.footer()}
    `;
  }

  render() {
    switch (this.phase) {
      case 'gate':
        return this.renderGate();
      case 'landing':
        return this.renderLanding();
      case 'preloading':
        return this.renderPreloading();
      case 'deck':
        return this.deck ? this.renderDeck() : this.renderLoading();
      case 'not-found':
        return this.renderStateCard(
          'Link not available',
          "This share link isn’t valid — it may have been revoked.",
        );
      case 'expired':
        return this.renderStateCard('Link expired', 'This share link has expired.');
      case 'error':
        return this.renderStateCard(
          'Couldn’t open this deck',
          this.errorMsg ?? 'This share link isn’t valid.',
        );
      case 'loading':
      default:
        return this.renderLoading();
    }
  }

  /** The abstract Folio mark (stacked sheets + aperture + emerald edge),
   *  themed via currentColor + --brand-primary. Mirrors the favicon. */
  private folioMark(): TemplateResult {
    return html`
      <svg class="mark" viewBox="0 0 24 24" width="44" height="44" aria-hidden="true">
        <rect x="9.6" y="4.8" width="10.1" height="10.1" rx="2.2" fill="currentColor" opacity="0.22" />
        <rect x="7.7" y="6.7" width="10.1" height="10.1" rx="2.2" fill="currentColor" opacity="0.42" />
        <rect x="5.8" y="8.6" width="10.1" height="10.1" rx="2.2" fill="currentColor" />
        <rect x="8.9" y="11.8" width="3.8" height="3.8" rx="1.1" fill="var(--surface)" />
        <path
          d="M7.9 8.6 h5.3 a2.2 2.2 0 0 1 2.2 2.2"
          stroke="var(--brand-primary, currentColor)"
          stroke-width="1.2"
          stroke-linecap="round"
          fill="none"
        />
      </svg>
    `;
  }

  private renderLoading(): TemplateResult {
    return html`
      <div class="centre">
        <div class="brandload">
          ${this.folioMark()}
          <div class="brandload-bar indet"><span></span></div>
          <p class="brandload-text">Loading deck…</p>
        </div>
      </div>
      ${this.footer()}
    `;
  }

  private renderPreloading(): TemplateResult {
    const pct = this.preloadTotal ? Math.round((this.preloadDone / this.preloadTotal) * 100) : 0;
    return html`
      <div class="centre">
        <div class="brandload">
          ${this.folioMark()}
          <div class="brandload-bar"><span style=${`width:${pct}%`}></span></div>
          <p class="brandload-text">Preparing slides… ${this.preloadDone}/${this.preloadTotal}</p>
        </div>
      </div>
      ${this.footer()}
    `;
  }

  private renderStateCard(title: string, msg: string): TemplateResult {
    return html`
      <div class="centre">
        <div class="brandload">
          ${this.folioMark()}
          <h1 class="state-title">${title}</h1>
          <p class="brandload-text">${msg}</p>
        </div>
      </div>
      ${this.footer()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'shared-deck-page': SharedDeckPage;
  }
}
